import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const POLL_MS = 3000;

// A card-platform sync (Ramp, Corpay) runs in the background on the server
// (2026-10-22) — Ramp downloads every receipt, so a first sync can take
// minutes. start() kicks it off; the hook polls /sync-status, shows
// "syncing" until it's done, then reports the result and calls onDone.
// A sync already running when the page opens is picked up too.
export default function useCardSync(itype, onDone, enabled = true) {
  const [syncing, setSyncing] = useState(false);
  const timer = useRef(null);
  const done = useRef(onDone);
  done.current = onDone;

  const stop = () => { if (timer.current) clearTimeout(timer.current); timer.current = null; };

  const poll = useCallback(() => {
    stop();
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/contractor/integrations/${itype}/sync-status`);
        if (data.running) return poll();
        setSyncing(false);
        const r = data.result || {};
        if (data.status === "failed" || r.error) toast.error(`${itype === "ramp" ? "Ramp" : "Sync"}: ${r.error || "sync failed"}`, { duration: 8000 });
        else toast.success(`${itype === "ramp" ? "Ramp" : "Card"} sync done — ${r.imported ?? 0} new (${r.matched ?? 0} matched automatically), ${r.duplicates ?? 0} already here`);
        done.current?.();
      } catch {
        poll();
      }
    }, POLL_MS);
  }, [itype]);

  useEffect(() => {
    if (!enabled) return undefined;
    api.get(`/contractor/integrations/${itype}/sync-status`).then(({ data }) => {
      if (data.running) { setSyncing(true); poll(); }
    }).catch(() => {});
    return stop;
  }, [itype, enabled, poll]);

  const start = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/contractor/integrations/${itype}/sync`);
      toast.info(data.already_running ? "A sync is already running — it'll finish in the background." : "Sync started — it runs in the background; you can keep working.");
      poll();
    } catch (err) {
      setSyncing(false);
      toast.error(err.response?.data?.detail || "Sync failed to start");
    }
  };

  return { syncing, start };
}
